import {t} from '@lingui/macro';
import {isEqual, partition} from 'lodash';
import {useEffect, useMemo, useReducer} from 'react';

import {FrontClickEvent} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {isDefined} from '../../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {useConstant} from '../../../../../../srcCommon/helpers/hookHelpers';
import {SearchQueryPart, splitSearchQuery} from '../../../../../../srcCommon/inboxes/search/searchHelpers';
import {ModifiersEnum} from '../../../../../../srcCommon/inboxes/search/searchTypes';
import {useLocalize} from '../../../../../../srcCommon/internationalization/internationalizationContext';
import {CancelToken} from '../../../../../helpers/asyncCancelHelpers';
import {useAppDispatch, useAppSelector} from '../../../../../helpers/reduxHelpers';
import {AccountModel} from '../../../../../models/api/accountModel';
import {SearchTagModel} from '../../../../../models/api/searchTagModel';
import {fetchAccountsByQueryV2AsyncAction} from '../../../../../store/data/accounts/accountsActions';
import {Localizer} from '../../../../../store/stateless/internationalizationSelectors';
import {DropdownFetcher} from '../../../../common/dropdowns/source/asyncDropdownSource';
import {
  trackSearchQueryBuilderResetClick,
  trackSearchQueryBuilderSearchClick,
} from '../../searchTrackingHelpers';
import {
  getFetchTagsAsync,
  makeRenderAccountRow,
  makeRenderTagRow,
  maybeRemoveDoubleQuotes,
  maybeWrapWithDoubleQuotes,
  statusDropdownItems,
} from './inboxesSearchQueryBuilderHelpers';
import {
  InboxesSearchQueryBuilderInputConfig,
  InboxesSearchQueryBuilderInputsEnum,
  InputValue,
} from './inboxesSearchQueryBuilderTypes';

/*
 * Constants.
 */

const fetchAccountsLimit = 20;

/*
 * Types.
 */

export interface Values {
  [field: string]: InputValue;
}

export interface FormState {
  values: Values;
}

interface FormConfig {
  searchQuery: string;
  namespace?: string;
  queryHash?: string;
}

interface AccountsFilterPaginationData {
  nextPageToken?: string;
}

/*
 * Actions.
 */

export enum FormActionsEnum {
  RESET_VALUES = '@@form/reset_values',
  SET_FIELD_VALUE = '@@form/set_field_value',
  SET_VALUES = '@@form/set_values',
}

interface ResetValuesAction {
  type: FormActionsEnum.RESET_VALUES;
}

interface SetFieldValueAction {
  type: FormActionsEnum.SET_FIELD_VALUE;
  payload: {
    field: keyof Values;
    value: InputValue;
  };
}

interface SetValuesAction {
  type: FormActionsEnum.SET_VALUES;
  payload: {
    values: Values;
  };
}

type FormAction = SetFieldValueAction | ResetValuesAction | SetValuesAction;

/*
 * Reducer.
 */

export function formReducer(state: FormState, action: FormAction) {
  switch (action.type) {
    case FormActionsEnum.SET_FIELD_VALUE:
      return {
        ...state,
        values: {
          ...state.values,
          [action.payload.field]: action.payload.value,
        },
      };

    case FormActionsEnum.RESET_VALUES:
      return {
        ...state,
        values: Object.entries(state.values).reduce(
          (acc, [key, inputValue]) => ({
            ...acc,
            [key]: {
              ...inputValue,
              value: '',
            },
          }),
          {},
        ),
      };

    case FormActionsEnum.SET_VALUES:
      return {
        ...state,
        values: action.payload.values,
      };

    default:
      return state;
  }
}

function makeValuesFromQueryParts(
  queryParts: ReadonlyArray<SearchQueryPart>,
  inputsConfig: ReadonlyArray<InboxesSearchQueryBuilderInputConfig>,
): Values {
  return inputsConfig.reduce(
    (acc, curr) => ({
      ...acc,
      [curr.id]: makeInputPropsForModifier(queryParts, curr.modifier),
    }),
    {},
  );
}

/*
 * Custom hook.
 */

export type QueryBuilderForm = ReturnType<typeof useInboxesSearchQueryBuilderForm>;
export function useInboxesSearchQueryBuilderForm(config: FormConfig) {
  const {searchQuery, namespace, queryHash} = config;

  const localize = useLocalize();
  const dispatch = useAppDispatch();

  const tagsFetcher = useAppSelector(getFetchTagsAsync);

  // @TODO: Move this fetcher elsewhere.
  const accountsFetcher: DropdownFetcher<AccountModel, AccountsFilterPaginationData> = async (
    query: string,
    accounts: ReadonlyArray<AccountModel>,
    cancelToken: CancelToken,
    paginationData?: AccountsFilterPaginationData,
  ) => {
    const response = await dispatch(
      fetchAccountsByQueryV2AsyncAction({
        query,
        limit: fetchAccountsLimit,
        cancelToken,
        nextPageToken: paginationData?.nextPageToken,
      }),
    );
    cancelToken.throwIfRequested();

    return {
      items: accounts.concat(response.accounts),
      canLoadMore: Boolean(response.nextPageToken),
      paginationData: {
        nextPageToken: response.nextPageToken,
      },
    };
  };

  const inputsConfig = useConstant(() =>
    makeFormInputsConfig({localize, tagsFetcher, accountsFetcher, namespace}),
  );

  const [searchQueryParts, searchQueryValues] = useMemo(() => {
    const parts = splitSearchQuery(searchQuery);
    const values = makeValuesFromQueryParts(parts, inputsConfig);
    return [parts, values];
    // inputsConfig is not supposed to change, it's part of the deps to keep useMemo happy.
  }, [inputsConfig, searchQuery]);

  // Here, the values will only be set on the first call to the hook.
  const [formState, formDispatch] = useReducer(formReducer, {
    values: searchQueryValues,
  });

  const setValues = (values: Values) => {
    formDispatch({
      type: FormActionsEnum.SET_VALUES,
      payload: {
        values,
      },
    });
  };

  // Overwrite values when searchQuery changes.
  useEffect(() => setValues(searchQueryValues), [searchQueryValues]);

  const setFieldValue = (field: keyof Values, value: InputValue) => {
    formDispatch({
      type: FormActionsEnum.SET_FIELD_VALUE,
      payload: {
        field,
        value,
      },
    });
  };

  const handleChange = (field: keyof Values) => (value: InputValue) => setFieldValue(field, value);

  const handleSubmit = (event?: FrontClickEvent) => {
    const initialValues = searchQueryValues;

    const diffValues: Values = Object.entries(formState.values).reduce((acc, [key, inputValue]) => {
      const initialValue = initialValues[key];

      if (initialValue.value === inputValue.value) {
        return acc;
      }

      return {
        ...acc,
        [key]: inputValue,
      };
    }, {});

    // Track.
    trackSearchQueryBuilderSearchClick({
      queryHash,
      changedInputs: Object.keys(diffValues),
    });

    const [inputsToUpdate, inputsToAdd] = partition(diffValues, (input) => isDefined(input.queryPart));

    const newSearchQuery = [
      // Maybe replace existing query parts.
      searchQueryParts
        .map((searchQueryPart) => {
          const maybeUpdatedInput = inputsToUpdate.find((input) => isEqual(input.queryPart, searchQueryPart));

          if (maybeUpdatedInput) {
            return onInputSubmit(maybeUpdatedInput);
          }

          const {text, token} = searchQueryPart;

          // Using token.value allows to remove useless spaces.
          if (token?.modifier) {
            return `${token.modifier}:${token.value}`;
          }

          return text;
        })
        .join(''),
      // Maybe add new terms to the query.
      ...inputsToAdd.map(onInputSubmit),
    ]
      .filter((str) => str.length > 0)
      .join(' ');

    return newSearchQuery;
  };

  const handleReset = (event?: FrontClickEvent) => {
    formDispatch({
      type: FormActionsEnum.RESET_VALUES,
    });

    // Track.
    trackSearchQueryBuilderResetClick({queryHash});
  };

  // Disable `reset` and `search` buttons if no input is filled.
  const areFormButtonsDisabled = isFormEmpty(formState);

  return {
    inputsConfig,
    ...formState,
    handleChange,
    handleReset,
    handleSubmit,
    setFieldValue,
    areFormButtonsDisabled,
  };
}

/*
 * Helpers.
 */

interface FormInputsConfigProps {
  localize: Localizer;
  tagsFetcher: DropdownFetcher<SearchTagModel>;
  accountsFetcher: DropdownFetcher<AccountModel>;
  namespace?: string;
}

// @NOTE: Order matters!
function makeFormInputsConfig(
  props: FormInputsConfigProps,
): ReadonlyArray<InboxesSearchQueryBuilderInputConfig> {
  const {localize, tagsFetcher, accountsFetcher, namespace} = props;

  return [
    {
      id: InboxesSearchQueryBuilderInputsEnum.FROM,
      modifier: ModifiersEnum.FROM,
      icon: 'messageSmall',
      label: localize(t('search-query-builder-from')`From`),
      placeholder: localize(t('search-query-builder-from-placeholder')`Add email`),
      namespace,
    },
    {
      id: InboxesSearchQueryBuilderInputsEnum.TO,
      modifier: ModifiersEnum.TO,
      icon: 'messageSmall',
      label: localize(t('search-query-builder-to')`To / Cc / Bcc`),
      placeholder: localize(t('search-query-builder-to-placeholder')`Add email`),
      namespace,
    },
    {
      id: InboxesSearchQueryBuilderInputsEnum.SUBJECT,
      modifier: ModifiersEnum.SUBJECT,
      icon: 'contentSmall',
      label: localize(t('search-query-builder-subject')`Subject`),
      placeholder: localize(t('search-query-builder-subject-placeholder')`Add subject`),
    },
    {
      id: InboxesSearchQueryBuilderInputsEnum.BODY_CONTAINS,
      modifier: ModifiersEnum.BODY,
      icon: 'contentSmall',
      label: localize(t('search-query-builder-body')`Message body contains`),
      placeholder: localize(t('search-query-builder-body-placeholder')`Add words`),
    },
    {
      id: InboxesSearchQueryBuilderInputsEnum.STATUS,
      modifier: ModifiersEnum.IS,
      icon: 'archiveSmall',
      label: localize(t('search-query-builder-status')`Status`),
      placeholder: localize(t('search-query-builder-status-placeholder')`Select status`),
      title: localize(t('search-query-builder-status-dropdown-title')`Status`),
      items: statusDropdownItems(),
    },
    {
      id: InboxesSearchQueryBuilderInputsEnum.TAG,
      modifier: ModifiersEnum.TAG,
      icon: 'tagSmall',
      label: localize(t('search-query-builder-tag')`Tag`),
      placeholder: localize(t('search-query-builder-tag-placeholder')`Select tag`),
      title: localize(t('search-query-builder-tag-dropdown-title')`Tags`),
      fetcher: tagsFetcher,
      makeRowRenderer: makeRenderTagRow,
    },
    {
      id: InboxesSearchQueryBuilderInputsEnum.ACCOUNT,
      modifier: ModifiersEnum.ACCOUNT,
      icon: 'briefcaseOutlineSmall',
      label: localize(t('search-query-builder-account')`Account`),
      placeholder: localize(t('search-query-builder-account-placeholder')`Select account`),
      title: localize(t('search-query-builder-account-dropdown-title')`Accounts`),
      fetcher: accountsFetcher,
      makeRowRenderer: makeRenderAccountRow,
    },
  ];
}

function makeInputPropsForModifier(
  searchQueryParts: ReadonlyArray<SearchQueryPart>,
  modifier: ModifiersEnum,
): InputValue {
  const queryPart = searchQueryParts.find(({token}) => token?.modifier === modifier);

  return {
    queryPart,
    modifier,
    value: maybeRemoveDoubleQuotes(queryPart?.token?.value),
  };
}

function onInputSubmit(inputValue: InputValue) {
  return inputValue.value.trim().length > 0
    ? `${inputValue.modifier}:${maybeWrapWithDoubleQuotes(inputValue.value)}`
    : '';
}

function isFormEmpty(formState: FormState) {
  return Object.values(formState.values).every(({value}) => value.length === 0);
}
